import { createContext, type ReactNode } from 'react';
import { useSupportedCurrencies } from '@/api/hooks/useUserSettings';
import { useAuth } from '@/contexts/AuthContext';

/** Currency code => fraction digits, or null while unavailable. */
export const CurrencyPrecisionContext = createContext<Record<string, number> | null>(null);

/**
 * Loads the server's currency precision table once for the whole app.
 *
 * The table is static reference data, so a single request feeds every money
 * component instead of each one subscribing to its own query. Consumers fall back
 * to locally derived precision (see useCurrencyFractionDigits), so a slow or failed
 * request degrades formatting rather than blocking forms from submitting.
 */
export function CurrencyPrecisionProvider({ children }: { children: ReactNode }) {
    const { isAuthenticated } = useAuth();
    const { data } = useSupportedCurrencies(isAuthenticated);

    return (
        <CurrencyPrecisionContext.Provider value={data?.fractionDigits ?? null}>
            {children}
        </CurrencyPrecisionContext.Provider>
    );
}
